import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { usePublicClient } from 'wagmi';
import { PANCAKE_ROUTER_V2, PANCAKE_ROUTER_V2_ABI, WBNB_ADDRESS, USDT_ADDRESS } from '../config/contracts';

const priceCache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

export const useTokenUsdPrice = (token) => {
  const [usdPrice, setUsdPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const publicClient = usePublicClient();

  useEffect(() => {
    if (!token || !publicClient) {
      setUsdPrice(null);
      return;
    }

    const fetchPrice = async () => {
      const cacheKey = token.isNative ? 'native' : token.address.toLowerCase();
      const cached = priceCache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setUsdPrice(cached.price);
        return;
      }

      // USDT/USDC = $1
      if (['USDT', 'USDC'].includes(token.symbol?.toUpperCase())) {
        setUsdPrice(1);
        priceCache.set(cacheKey, { price: 1, timestamp: Date.now() });
        return;
      }

      setLoading(true);
      try {
        const provider = new ethers.BrowserProvider(publicClient);
        const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_V2_ABI, provider);

        let path;
        if (token.isNative) {
          path = [WBNB_ADDRESS, USDT_ADDRESS];
        } else {
          path = [token.address, WBNB_ADDRESS, USDT_ADDRESS];
        }

        const amountIn = ethers.parseUnits('1', token.decimals || 18);
        const amounts = await router.getAmountsOut(amountIn, path);
        const usdOut = amounts[amounts.length - 1];
        const price = Number(ethers.formatUnits(usdOut, 18));

        setUsdPrice(price);
        priceCache.set(cacheKey, { price, timestamp: Date.now() });
      } catch (error) {
        console.error('Error fetching USD price:', error);
        setUsdPrice(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPrice();
    const interval = setInterval(fetchPrice, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [token, publicClient]);

  return { usdPrice, loading };
};
